import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { usePuck } from "@measured/puck";
import { useQueries, useQuery } from "@tanstack/react-query";
import { listPuckPages, loadPuckPage } from "@/api/cms-puck/api";
import { usePuckEditorLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import {
  normalizeSelectionCategories,
  type SelectionCategory,
} from "@/lib/cms-puck/blocks/selection";

export type SelectionCategoryPreviewValue = {
  categories: SelectionCategory[];
  /** Rubrique résolue pour l'aperçu (1re si id manquant / invalide). */
  category: SelectionCategory | null;
  previewCategoryId: string | null;
  isLoading: boolean;
};

const SelectionCategoryPreviewContext =
  createContext<SelectionCategoryPreviewValue>({
    categories: [],
    category: null,
    previewCategoryId: null,
    isLoading: false,
  });

function extractCategoriesFromLayout(data: unknown): SelectionCategory[] {
  if (!data || typeof data !== "object") return [];
  const content = (data as { content?: unknown }).content;
  if (!Array.isArray(content)) return [];
  const out: SelectionCategory[] = [];
  const walk = (nodes: unknown[]) => {
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const n = node as {
        type?: string;
        props?: { categories?: unknown; [key: string]: unknown };
      };
      if (n.type === "SelectionSections" && n.props) {
        out.push(...normalizeSelectionCategories(n.props.categories));
      }
      if (n.props) {
        for (const v of Object.values(n.props)) {
          if (
            Array.isArray(v) &&
            v.every(
              (x) =>
                x != null &&
                typeof x === "object" &&
                "type" in (x as object),
            )
          ) {
            walk(v);
          }
        }
      }
    }
  };
  walk(content);
  return out;
}

function findDetailPreviewCategoryId(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const content = (data as { content?: unknown }).content;
  if (!Array.isArray(content)) return null;

  const walk = (nodes: unknown[]): string | null => {
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const n = node as {
        type?: string;
        props?: { previewCategoryId?: unknown; [key: string]: unknown };
      };
      if (n.type === "SelectionCategoryDetail") {
        const id = n.props?.previewCategoryId;
        return typeof id === "string" && id.trim() ? id.trim() : null;
      }
      if (n.props) {
        for (const v of Object.values(n.props)) {
          if (
            Array.isArray(v) &&
            v.every(
              (x) =>
                x != null &&
                typeof x === "object" &&
                "type" in (x as object),
            )
          ) {
            const found = walk(v);
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  return walk(content);
}

/**
 * Provider monté dans `overrides.puck` : charge les rubriques des pages
 * SelectionSections du CMS et expose la catégorie d'aperçu (sélecteur ou 1re).
 */
export function SelectionCategoryPreviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  // `usePuck()` expose `appState.data` (pas `data`) — c'est la source du
  // `previewCategoryId` persisté sur le bloc Detail.
  const { appState } = usePuck();
  const puckData = appState.data;
  const locale = usePuckEditorLocale() ?? "fr";

  const pagesQuery = useQuery({
    queryKey: ["cms-puck", "pages", "selection-categories-source"],
    queryFn: () =>
      listPuckPages({
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
        excludeSearchFlow: true,
        excludeMaintenancePage: true,
        excludeAbonnementPage: true,
      }),
  });

  const candidateIds = useMemo(() => {
    const pages = pagesQuery.data ?? [];
    const selection = pages.filter((p) => p.sourceTemplateKey === "selection");
    const pool = selection.length > 0 ? selection : pages;
    return pool.slice(0, 12).map((p) => p.id);
  }, [pagesQuery.data]);

  const pageQueries = useQueries({
    queries: candidateIds.map((id) => ({
      queryKey: ["cms-puck", "page", id, locale, "sel-cats"],
      queryFn: () => loadPuckPage(id, locale),
      enabled: candidateIds.length > 0,
      staleTime: 30_000,
    })),
  });

  const pageQueryDataKey = pageQueries
    .map((q) => (q.dataUpdatedAt ? String(q.dataUpdatedAt) : q.status))
    .join("|");

  const categories = useMemo(() => {
    const byId = new Map<string, SelectionCategory>();
    for (const q of pageQueries) {
      const data = q.data?.data;
      for (const cat of extractCategoriesFromLayout(data)) {
        if (!byId.has(cat.id)) byId.set(cat.id, cat);
      }
    }
    return Array.from(byId.values());
    // pageQueryDataKey : invalide quand les layouts chargés changent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageQueryDataKey]);

  const storedPreviewId = findDetailPreviewCategoryId(puckData);

  const category = useMemo(() => {
    if (categories.length === 0) return null;
    if (storedPreviewId) {
      const found = categories.find((c) => c.id === storedPreviewId);
      if (found) return found;
    }
    return categories[0] ?? null;
  }, [categories, storedPreviewId]);

  const pagesLoading =
    pagesQuery.isLoading || pageQueries.some((q) => q.isLoading);

  const value = useMemo<SelectionCategoryPreviewValue>(
    () => ({
      categories,
      category,
      previewCategoryId: category?.id ?? storedPreviewId,
      isLoading: pagesLoading,
    }),
    [categories, category, storedPreviewId, pagesLoading],
  );

  return (
    <SelectionCategoryPreviewContext.Provider value={value}>
      {children}
    </SelectionCategoryPreviewContext.Provider>
  );
}

export function useSelectionCategoryPreview(): SelectionCategoryPreviewValue {
  return useContext(SelectionCategoryPreviewContext);
}
